/*
 * BS.Web.Blog: the "Embed this tool" offer on a tool page. The TypeScript source of the static web asset
 * _content/BS.Web.Blog/bs-embed.js (wwwroot/bs-embed.js, compiled on build to ES2019 with a source map):
 *
 *   <script src="/_content/BS.Web.Blog/bs-embed.js" defer></script>
 *
 * BlogTool.EmbedOffer writes the offer; a report chart's embed offer, the report's cite box (BlogReportPage) and the badge offer
 * (BlogBadges.Offer) write the same contract, one element with data-bs-embed per field. The script only makes the copying easier:
 *
 *   <section data-bs-embed>
 *     <textarea data-bs-embed-code readonly>&lt;iframe ...&gt;</textarea>          the code, selectable without the script
 *     <button type="button" data-bs-embed-copy data-bs-copied="Copied" hidden>Copy code</button>
 *     <span data-bs-embed-status role="status"></span>                           says "Copied" for a few seconds
 *   </section>
 *
 * The field selects all of its code when it gets the focus. Where the browser lets a page write to the clipboard (a secure page
 * with navigator.clipboard), the button shows and copies the code; when that fails, the field gets the focus with its code
 * selected, ready to copy by hand. Without the script the button stays hidden and the code is copied by hand. No dependencies, no
 * inline code and no eval: it runs under a strict Content-Security-Policy (script-src 'self'); a second copy on the page does nothing.
 *
 * One job per part (SOLID): CodeField reads and selects the code (ICodeField); BrowserClipboard writes text to the clipboard
 * (IClipboard); StatusMessage says what happened (IStatus); CopyButton shows the button and runs a copy on a click; CopyOffer
 * knows only these contracts and the order of things, and start() wires one CopyOffer per offer. All of it lives in one function
 * scope: nothing is left in the global scope.
 */
(() => {
  /** The offer's attribute. */
  const OFFER = "data-bs-embed";

  /** The field with the code. */
  const CODE = "data-bs-embed-code";

  /** The copy button; its data-bs-copied names what the status says after a copy. */
  const COPY = "data-bs-embed-copy";

  /** The words for a done copy, on the button. */
  const COPIED = "data-bs-copied";

  /** The live region the result is said in. */
  const STATUS = "data-bs-embed-status";

  /** How long the status keeps its words, in milliseconds. */
  const SHOWN = 4000;

  /** Marks an offer that is wired, so a second copy of the script on the page leaves it alone. */
  const WIRED = Symbol.for("bs-web-blog-embed");

  /** The code to copy. */
  interface ICodeField {
    /** The code as it stands in the field. */
    text(): string;

    /** Selects the whole code whenever the field gets the focus. */
    selectOnFocus(): void;

    /** Moves the focus to the field with its code selected, for copying by hand. */
    offer(): void;
  }

  /** Writes text to the clipboard; rejects when the browser does not let it. */
  interface IClipboard {
    write(text: string): Promise<void>;
  }

  /** Says what happened, to everyone: in the live region, for a few seconds. */
  interface IStatus {
    say(words: string): void;
  }

  /** The field (a textarea or an input) that holds the code. */
  class CodeField implements ICodeField {
    public constructor(private readonly element: HTMLTextAreaElement | HTMLInputElement) {}

    public text(): string {
      return this.element.value;
    }

    public selectOnFocus(): void {
      this.element.addEventListener("focus", () => this.element.select());
    }

    public offer(): void {
      this.element.focus();
      this.element.select();
    }
  }

  /** The browser's asynchronous clipboard. */
  class BrowserClipboard implements IClipboard {
    public constructor(private readonly clipboard: Clipboard) {}

    public write(text: string): Promise<void> {
      return this.clipboard.writeText(text);
    }
  }

  /** The offer's live region; its words go again after a few seconds. */
  class StatusMessage implements IStatus {
    private timer = 0;

    public constructor(
      private readonly element: Element | null,
      private readonly clock: Window,
    ) {}

    public say(words: string): void {
      const element = this.element;
      if (element === null) {
        return;
      }
      element.textContent = words;
      this.clock.clearTimeout(this.timer);
      this.timer = this.clock.setTimeout(() => {
        element.textContent = "";
      }, SHOWN);
    }
  }

  /** The copy button: shown once it can copy, a click asks for one. */
  class CopyButton {
    public constructor(private readonly element: HTMLElement) {}

    /** The words the status says after a copy. */
    public copied(): string {
      return this.element.getAttribute(COPIED) || "";
    }

    public onClick(copy: () => void): void {
      this.element.hidden = false;
      this.element.addEventListener("click", copy);
    }
  }

  /** One offer: the field selects its code on focus, and the button copies it or, failing that, hands the field over. */
  class CopyOffer {
    public constructor(
      private readonly field: ICodeField,
      private readonly button: CopyButton | null,
      private readonly clipboard: IClipboard | null,
      private readonly status: IStatus,
    ) {}

    public start(): void {
      this.field.selectOnFocus();
      const button = this.button;
      if (button !== null && this.clipboard !== null) {
        button.onClick(() => this.copy(button.copied()));
      }
    }

    private copy(copied: string): void {
      if (this.clipboard === null) {
        return;
      }
      void this.clipboard.write(this.field.text()).then(
        () => this.status.say(copied),
        () => this.field.offer(),
      );
    }
  }

  /** The clipboard when the page may write to it (a secure page with the asynchronous clipboard), else null. */
  function clipboard(): IClipboard | null {
    return window.isSecureContext && typeof navigator.clipboard === "object" && navigator.clipboard !== null
      ? new BrowserClipboard(navigator.clipboard)
      : null;
  }

  /** Wires one CopyOffer for every offer on the page that has its field; an offer is wired once. */
  function start(): void {
    const offers = document.querySelectorAll(`[${OFFER}]`);
    for (let i = 0; i < offers.length; i++) {
      const offer = offers[i];
      const code = offer.querySelector(`[${CODE}]`);
      if (WIRED in offer || !(code instanceof HTMLTextAreaElement || code instanceof HTMLInputElement)) {
        continue;
      }
      Object.defineProperty(offer, WIRED, { value: true });
      const button = offer.querySelector(`[${COPY}]`);
      new CopyOffer(
        new CodeField(code),
        button instanceof HTMLElement ? new CopyButton(button) : null,
        clipboard(),
        new StatusMessage(offer.querySelector(`[${STATUS}]`), window),
      ).start();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
